<template>
  <div :class="wrapperClasses">
    <label v-if="label && !floating" :for="textareaId" class="textarea__label">
      {{ label }}
      <span v-if="required" class="textarea__required">*</span>
    </label>

    <div class="textarea__container">
      <textarea
        :id="textareaId"
        ref="textarea"
        v-model="internalValue"
        :placeholder="floating ? ' ' : placeholder"
        :disabled="disabled"
        :readonly="readonly"
        :maxlength="maxlength"
        :rows="rows"
        class="textarea__field"
        @input="onInput"
        @focus="onFocus"
        @blur="onBlur"
        @keydown="$emit('keydown', $event)"
      ></textarea>

      <!-- Floating Label -->
      <label v-if="label && floating" :for="textareaId" class="textarea__floating-label">
        {{ label }}
        <span v-if="required" class="textarea__required">*</span>
      </label>
    </div>

    <div class="textarea__footer">
      <div v-if="hint && !error" class="textarea__hint">{{ hint }}</div>
      <div v-if="error && errorMessage" class="textarea__error">{{ errorMessage }}</div>
      <div v-if="maxlength && showCounter" class="textarea__counter">
        {{ currentLength }}/{{ maxlength }}
      </div>
    </div>
  </div>
</template>

<script>
let textareaIdCounter = 0

export default {
  name: 'BaseTextarea',
  props: {
    modelValue: {
      type: [String, Number],
      default: ''
    },
    label: {
      type: String,
      default: ''
    },
    placeholder: {
      type: String,
      default: ''
    },
    rows: {
      type: [Number, String],
      default: 3
    },
    floating: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    readonly: {
      type: Boolean,
      default: false
    },
    required: {
      type: Boolean,
      default: false
    },
    error: {
      type: Boolean,
      default: false
    },
    errorMessage: {
      type: String,
      default: ''
    },
    hint: {
      type: String,
      default: ''
    },
    maxlength: {
      type: [Number, String],
      default: null
    },
    showCounter: {
      type: Boolean,
      default: false
    },
    autoGrow: {
      type: Boolean,
      default: false
    },
    autofocus: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update:modelValue', 'focus', 'blur', 'keydown'],
  data() {
    return {
      textareaId: `textarea-${++textareaIdCounter}`,
      internalValue: this.modelValue,
      isFocused: false
    }
  },
  computed: {
    wrapperClasses() {
      return [
        'textarea-wrapper',
        {
          'textarea-wrapper--floating': this.floating,
          'textarea-wrapper--disabled': this.disabled,
          'textarea-wrapper--error': this.error,
          'textarea-wrapper--focused': this.isFocused,
          'textarea-wrapper--has-value': !!this.internalValue
        }
      ]
    },
    currentLength() {
      return this.internalValue ? String(this.internalValue).length : 0
    }
  },
  watch: {
    modelValue(newVal) {
      this.internalValue = newVal
      if (this.autoGrow) {
        this.$nextTick(this.adjustHeight)
      }
    }
  },
  mounted() {
    if (this.autofocus) {
      this.$nextTick(() => {
        this.$refs.textarea?.focus()
      })
    }
    if (this.autoGrow) {
      this.adjustHeight()
    }
  },
  methods: {
    onInput(e) {
      this.$emit('update:modelValue', e.target.value)
      if (this.autoGrow) {
        this.adjustHeight()
      }
    },
    onFocus(e) {
      this.isFocused = true
      this.$emit('focus', e)
    },
    onBlur(e) {
      this.isFocused = false
      this.$emit('blur', e)
    },
    adjustHeight() {
      const textarea = this.$refs.textarea
      if (textarea) {
        textarea.style.height = 'auto'
        textarea.style.height = textarea.scrollHeight + 'px'
      }
    },
    focus() {
      this.$refs.textarea?.focus()
    },
    blur() {
      this.$refs.textarea?.blur()
    }
  }
}
</script>

<style lang="scss" scoped>
@use '../../scss/variables' as *;

.textarea-wrapper {
  display: flex;
  flex-direction: column;
  width: 100%;

  &--disabled {
    opacity: 0.6;
    .textarea__container {
      background-color: $color-gray-100;
      cursor: not-allowed;
    }
    .textarea__field {
      cursor: not-allowed;
    }
  }

  &--error .textarea__container {
    border-color: $color-danger;
  }

  &--error.textarea-wrapper--focused .textarea__container {
    box-shadow: 0 0 0 3px rgba($color-danger, 0.15);
  }

  &--focused .textarea__container {
    border-color: $color-primary;
    box-shadow: 0 0 0 3px $input-focus-ring-color;
  }
}

.textarea__label {
  display: block;
  margin-bottom: $spacing-2;
  font-size: $font-size-sm;
  font-weight: $font-weight-medium;
  color: $color-text-primary;
}

.textarea__required {
  color: $color-danger;
  margin-left: 2px;
}

.textarea__container {
  position: relative;
  background-color: $color-bg-primary;
  border: $input-border-width solid $input-border-color;
  border-radius: $input-border-radius;
  transition: all $transition-normal $transition-timing;
  overflow: hidden;
}

.textarea__field {
  width: 100%;
  min-height: 80px;
  padding: 12px 14px;
  border: none;
  outline: none;
  background: transparent;
  font-family: $font-family-base;
  font-size: $font-size-sm;
  color: $color-text-primary;
  line-height: $line-height-normal;
  resize: vertical;

  &::placeholder {
    color: $color-text-tertiary;
  }
}

.textarea__footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: $spacing-1;
  min-height: 18px;
}

.textarea__hint {
  font-size: $font-size-xs;
  color: $color-text-tertiary;
}

.textarea__error {
  font-size: $font-size-xs;
  color: $color-danger;
}

.textarea__counter {
  font-size: $font-size-xs;
  color: $color-text-tertiary;
  margin-left: auto;
}

// ===============================
// Floating Label Styles
// ===============================
.textarea-wrapper--floating {
  .textarea__field {
    padding-top: 20px;

    &::placeholder {
      opacity: 0;
      transition: opacity $transition-normal;
    }
  }

  .textarea__floating-label {
    position: absolute;
    left: 14px;
    top: 16px;
    color: $color-text-tertiary;
    font-size: $font-size-sm;
    font-weight: $font-weight-normal;
    pointer-events: none;
    transition: all $transition-normal $transition-timing;
    background: transparent;
    padding: 0 4px;
    line-height: 1;
    z-index: 1;
  }

  &.textarea-wrapper--focused,
  &.textarea-wrapper--has-value {
    .textarea__field::placeholder {
      opacity: 1;
    }

    .textarea__floating-label {
      top: 0;
      transform: translateY(-50%);
      font-size: 11px;
      font-weight: $font-weight-medium;
      color: $color-primary;
      background: $color-bg-primary;
    }
  }

  &.textarea-wrapper--has-value:not(.textarea-wrapper--focused) {
    .textarea__floating-label {
      color: $color-text-secondary;
    }
  }

  &.textarea-wrapper--error {
    .textarea__floating-label {
      color: $color-danger;
    }
  }
}

// Responsive - prevent zoom on iOS
@media (max-width: $breakpoint-sm) {
  .textarea__field {
    font-size: 16px;
  }
}
</style>
